import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, RefreshCw, Users, DollarSign, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;


export default function AdminBillingPanel({ adminToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/billing/overview`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setData(res.data);
    } catch (e) {
      alert(`Could not load billing overview: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (adminToken) load(); }, [adminToken]);

  const setPlan = async (practiceId, tier) => {
    setBusyId(practiceId);
    try {
      await axios.post(`${API}/api/admin/billing/set-plan`,
        { practice_id: practiceId, tier, note: `Admin override (${new Date().toISOString()})` },
        { headers: { Authorization: `Bearer ${adminToken}` } });
      await load();
    } catch (e) {
      alert(`Failed to change tier: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const addPack = async (practiceId, packId) => {
    if (!packId) return;
    setBusyId(practiceId);
    try {
      await axios.post(`${API}/api/admin/billing/add-pack`,
        { practice_id: practiceId, pack_id: packId },
        { headers: { Authorization: `Bearer ${adminToken}` } });
      await load();
    } catch (e) {
      alert(`Failed to add pack: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const removePack = async (practiceId, packId) => {
    setBusyId(practiceId);
    try {
      await axios.post(`${API}/api/admin/billing/remove-pack`,
        { practice_id: practiceId, pack_id: packId },
        { headers: { Authorization: `Bearer ${adminToken}` } });
      await load();
    } catch (e) {
      alert(`Failed to remove pack: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusyId(null);
    }
  };

  const rows = useMemo(() => {
    if (!data?.practices) return [];
    const needle = search.trim().toLowerCase();
    const filtered = needle
      ? data.practices.filter(p => (p.name || '').toLowerCase().includes(needle)
          || (p.owner_email || '').toLowerCase().includes(needle))
      : data.practices;
    return filtered;
  }, [data, search]);

  const totals = useMemo(() => {
    if (!data?.practices) return null;
    const t = { practices: 0, mrr: 0, patients: 0 };
    for (const p of data.practices) {
      t.practices += 1;
      t.mrr += p.monthly_price || 0;
      t.patients += p.active_patients || 0;
    }
    return t;
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Billing & Plans Overview</h2>
          <p className="text-sm text-slate-600">Period: <strong>{data.period}</strong></p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search practices…"
            className="w-56"
            data-testid="admin-billing-search"
          />
          <Button variant="outline" onClick={load} data-testid="admin-billing-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {totals && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SummaryCard icon={<Users className="h-5 w-5 text-blue-600" />}
                       label="Practices" value={totals.practices.toLocaleString()} />
          <SummaryCard icon={<DollarSign className="h-5 w-5 text-emerald-600" />}
                       label="Monthly recurring revenue"
                       value={`$${totals.mrr.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
          <SummaryCard icon={<Users className="h-5 w-5 text-purple-600" />}
                       label="Active patients across all offices"
                       value={totals.patients.toLocaleString()} />
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-billing-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">Practice</th>
                <th className="text-left px-4 py-2.5 font-semibold">Tier</th>
                <th className="text-right px-4 py-2.5 font-semibold">MRR</th>
                <th className="text-right px-4 py-2.5 font-semibold">Patients</th>
                <th className="text-left px-4 py-2.5 font-semibold">This month</th>
                <th className="text-left px-4 py-2.5 font-semibold">Packs</th>
                <th className="text-left px-4 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.practice_id} className="border-t border-slate-100" data-testid={`admin-billing-row-${p.practice_id}`}>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.owner_email}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <select
                      value={p.tier}
                      disabled={busyId === p.practice_id}
                      onChange={e => setPlan(p.practice_id, e.target.value)}
                      className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                      data-testid={`admin-tier-${p.practice_id}`}
                    >
                      {data.available_tiers.map(t => (
                        <option key={t.tier} value={t.tier}>{t.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    ${p.monthly_price.toFixed(2)}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono">
                    {p.active_patients.toLocaleString()}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-xs text-slate-600 space-y-0.5">
                      <div>SMS: <strong>{p.usage.sms}</strong></div>
                      <div>Email: <strong>{p.usage.email}</strong></div>
                      <div>AI: <strong>{p.usage.ai_chat}</strong></div>
                      <div>Smile: <strong>{p.usage.smile_designs}</strong></div>
                      <div>Chatbot: <strong>{p.usage.chatbot_sessions}</strong></div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1 max-w-[160px]">
                      {(p.patient_packs || []).map(pk => (
                        <button
                          key={pk}
                          onClick={() => removePack(p.practice_id, pk)}
                          disabled={busyId === p.practice_id}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-xs text-blue-700 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700"
                          title="Click to remove"
                          data-testid={`admin-remove-pack-${p.practice_id}-${pk}`}
                        >
                          {pk} ×
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <select
                      value=""
                      disabled={busyId === p.practice_id}
                      onChange={e => { addPack(p.practice_id, e.target.value); e.target.value = ''; }}
                      className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white"
                      data-testid={`admin-add-pack-${p.practice_id}`}
                    >
                      <option value="">+ Add pack</option>
                      {data.available_packs.map(pk => (
                        <option key={pk.pack_id} value={pk.pack_id}>
                          {pk.name} (${pk.monthly_price}/mo)
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div className="p-8 text-center text-slate-500 text-sm">
              <AlertTriangle className="h-5 w-5 mx-auto mb-2 text-slate-400" />
              No practices match your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function SummaryCard({ icon, label, value }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
      <div className="p-2 rounded-lg bg-slate-50">{icon}</div>
      <div>
        <div className="text-xs text-slate-500 font-medium">{label}</div>
        <div className="text-xl font-bold text-slate-900">{value}</div>
      </div>
    </div>
  );
}
